import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import EventCard from './EventCard';
import { Button } from '@/components/ui/button';
import { usePublishedEvents } from '@/hooks/useEvents';
import { getMinRacePrice, formatPrice } from '@/lib/price-utils';
import { calculatePricing } from '@/lib/pricing';

const difficultyToIntensity = {
  easy: 'low' as const,
  moderate: 'medium' as const,
  challenging: 'high' as const,
  extreme: 'extreme' as const,
};

const EventsSection = () => {
  const { data: events, isLoading } = usePublishedEvents();

  const mapped = useMemo(() => {
    const now = Date.now();
    const upcoming = (events || [])
      .filter((e) => new Date(e.event_date).getTime() > now && e.event_category !== 'community')
      .slice(0, 6);

    return upcoming.map((event) => {
      const maxDistance = event.races && event.races.length > 0
        ? Math.max(...event.races.map((r) => r.distance_km || 0))
        : event.distance_km;

      const displayPrice = getMinRacePrice({
        price_cents: event.price_cents,
        commission_rate: event.commission_rate,
        races: event.races?.map((r) => ({
          price_cents: r.price_cents,
          fees_included: r.fees_included,
        })),
      });

      let pricePerPerson = displayPrice;
      let hasTeamRace = false;
      if (event.races && event.races.length > 0) {
        const perPersonPrices = event.races
          .filter((r: any) => r.price_cents > 0)
          .map((r: any) => {
            const teamSize = r.team_mode === 'duo' ? 2
              : r.team_mode === 'trio' ? 3
              : r.team_mode === 'relay' ? (r.team_size || 1)
              : 1;
            if (teamSize > 1) hasTeamRace = true;
            const pricing = calculatePricing(r.price_cents, r.fees_included ?? true, event.commission_rate);
            return Math.round(pricing.runnerPays / teamSize);
          });
        if (perPersonPrices.length > 0) {
          pricePerPerson = Math.min(...perPersonPrices);
        }
      }

      return {
        id: event.id,
        slug: event.slug,
        title: event.title,
        date: format(new Date(event.event_date), 'd MMMM yyyy', { locale: fr }),
        location: `${event.city}, ${event.country}`,
        distance: maxDistance ? `${maxDistance} km` : 'N/A',
        participants: event.current_participants,
        price: formatPrice(pricePerPerson, event.currency),
        priceSuffix: hasTeamRace ? '/pers.' : undefined,
        image: event.image_url || 'https://images.unsplash.com/photo-1502224562085-639556652f33?w=800&auto=format&fit=crop',
        featured: event.featured,
        intensity: difficultyToIntensity[event.difficulty as keyof typeof difficultyToIntensity],
        terrain: event.terrain,
      };
    });
  }, [events]);

  return (
    <section id="events" className="relative py-24 md:py-32 border-t border-white/10 overflow-hidden bg-background">
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] items-end gap-8 mb-14">
          <div>
            <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-primary mb-4">
              // CHAPITRE DEUX · L'AGENDA DES FOUS
            </div>
            <h2 className="m-0 leading-[0.95]">
              <span className="font-display italic uppercase -skew-x-6 inline-block text-[48px] md:text-[72px] lg:text-[96px] text-foreground">
                HUIT MOIS,
              </span>
              <br />
              <span
                className="font-display italic uppercase -skew-x-6 inline-block text-[48px] md:text-[72px] lg:text-[96px] text-transparent bg-clip-text"
                style={{ backgroundImage: 'linear-gradient(90deg, #caff00 0%, #f8ff3a 35%, #ff7a1a 65%, #ff2d9e 100%)' }}
              >
                DES MILLIERS DE DÉPARTS.
              </span>
            </h2>
            <p className="mt-5 max-w-[540px] text-[15px] leading-relaxed text-muted-foreground">
              Six courses par week-end, une France qui ne s'arrête jamais de courir. Route, trail, urbain, nature — chaque surface a sa tribu.
            </p>
          </div>

          <div className="flex flex-col gap-3 lg:items-end">
            <Button
              variant="outline"
              asChild
              className="hidden md:inline-flex border-primary/60 text-primary hover:bg-primary/10 hover:border-primary"
            >
              <Link to="/explorer">
                Calendrier complet
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-primary animate-spin" />
          </div>
        )}

        {!isLoading && mapped.length === 0 && (
          <div className="py-16 text-center text-muted-foreground text-sm">
            Aucune course à venir pour le moment.
          </div>
        )}

        {!isLoading && mapped.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {mapped.map((event, i) => (
              <EventCard key={event.id || i} {...event} />
            ))}
          </div>
        )}

        <div className="text-center md:hidden mt-10">
          <Button
            variant="outline"
            asChild
            className="w-full border-primary/60 text-primary hover:bg-primary/10"
          >
            <Link to="/explorer">
              Calendrier complet
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default EventsSection;
